import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Button, Typography } from "@mui/material";
import AlertModal from "../../../components/alertModal/alertModal";
import CustomBreadcrumb from "../../../components/customBreadcrumb/CustomBreadcrumb";
import EmptyPlaceholder from "../../../components/emptyPlaceholder/EmptyPlaceholder";
import MainLoader from "../../../components/loading/MainLoader";
import SearchBar from "../../../components/searchBar/SearchBar";
import Title from "../../../components/title/Title";
import { getOrderDetailsByRefNo } from "../../../services/fault";
import { getCustomerReferenceNumber } from "../../../services/localStorage";
import inProgressIcon from "../../../assets/images/icons/faultProgress/loading sc.gif";
import completedIcon from "../../../assets/images/icons/faultProgress/completed.svg";
import disabledIcon from "../../../assets/images/icons/faultProgress/disabled.svg";
import { IOrderResult } from "../../../interfaces/orderDetails";
import styles from "./OrderProgress.module.scss";
import useMediaQuery from "@mui/material/useMediaQuery";
import TextInfo from "../../../components/textInfo/TextInfo";

const OrderProgress = () => {
  const { state } = useLocation();
  const isMobile = useMediaQuery("(max-width: 600px)");
  const [loading, setLoading] = useState<boolean>(false);
  const [orderDetails, setOrderDetails] = useState<IOrderResult>();
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  );
  const [searchFaultId, setSearchFaultId] = useState<string>();
  const [forceSearch, setForceSearch] = useState<boolean>(false);

  const navigate = useNavigate();

  useEffect(() => {
    if (state) {
      setSearchFaultId((state as any).srNumber);
    }
  }, [state]);

  useEffect(() => {
    if (searchFaultId) {
      setOrderDetails(undefined);
      const data = {
        orderNumber: searchFaultId.trim(),
        customerReferenceNumber: getCustomerReferenceNumber(),
      };
      setLoading(true);
      getOrderDetailsByRefNo(data)
        .then((data: IOrderResult) => {
          setOrderDetails(data);
          setLoading(false);
        })
        .catch((err) => {
          setErrorMessage(err.message);
          setLoading(false);
        });
    }
  }, [searchFaultId, forceSearch]);

  const searchHandler = (value: string) => {
    if (!value) {
      return;
    }

    // setorderDetails(undefined);

    setSearchFaultId(value);
    setForceSearch((prev) => !prev);
  };

  interface Step {
    number: number;
    title: string;
    description?: string;
    active: boolean;
    icon?: JSX.Element;
  }

  const steps: Step[] = [
    {
      number: 1,
      title: "Order Initiated",
      description: orderDetails?.orderCreatedDate,
      active: !!orderDetails?.status,
      icon: (
        <img
          className={styles.resizedGif}
          src={!!orderDetails?.status ? completedIcon : disabledIcon}
          alt="Created"
        />
      ),
    },
    {
      number: 2,
      title: "In progress",
      active: !!(
        orderDetails?.status?.includes("Abandoned") ||
        orderDetails?.status?.includes("Rejected") ||
        orderDetails?.status?.includes("Complete") ||
        orderDetails?.status?.includes("Pending") ||
        orderDetails?.status?.includes("ongoing") ||
        orderDetails?.status?.includes("Pending Cancel") ||
        orderDetails?.status?.includes("Submitted") ||
        orderDetails?.status?.includes("Verified") ||
        orderDetails?.status?.includes("Awaiting Approval") ||
        orderDetails?.status?.includes("Approved") ||
        orderDetails?.status?.includes("Cancelled")
      ),
      icon: (
        <img
          className={styles.resizedGif}
          src={
            orderDetails?.status?.includes("Abandoned")
              ? completedIcon
              : orderDetails?.status?.includes("Rejected")
              ? completedIcon
              : orderDetails?.status?.includes("Cancelled")
              ? completedIcon
              : orderDetails?.status?.includes("Pending")
              ? inProgressIcon
              : orderDetails?.status?.includes("ongoing")
              ? inProgressIcon
              : orderDetails?.status?.includes("Complete")
              ? completedIcon
              : orderDetails?.status?.includes("Pending Cancel")
              ? inProgressIcon
              : orderDetails?.status?.includes("Submitted")
              ? inProgressIcon
              : orderDetails?.status?.includes("Verified")
              ? inProgressIcon
              : orderDetails?.status?.includes("Awaiting Approval")
              ? inProgressIcon
              : orderDetails?.status?.includes("Approved")
              ? inProgressIcon
              : disabledIcon
          }
          alt="Assigned"
        />
      ),
    },
    {
      number: 3,
      title: "Completed",
      active: !!(
        orderDetails?.status?.includes("Rejected") ||
        orderDetails?.status?.includes("Abandoned") ||
        orderDetails?.status?.includes("Complete") ||
        orderDetails?.status?.includes("Cancelled")
      ),
      icon: (
        <img
          className={styles.resizedGif}
          src={
            orderDetails?.status?.includes("Abandoned")
              ? completedIcon
              : orderDetails?.status?.includes("Rejected")
              ? completedIcon
              : orderDetails?.status?.includes("Cancelled")
              ? completedIcon
              : orderDetails?.status?.includes("Complete")
              ? completedIcon
              : disabledIcon
          }
          alt="Resolved"
        />
      ),
    },
  ];

  return (
    <div className="page-container">
      <MainLoader show={loading} />
      <CustomBreadcrumb
        items={[
          { path: "/", title: "Home" },
          {
            path: "/tracker/status/orderProgress",
            title: "Order Status",
          },
        ]}
      />
      <Title>Order Status</Title>
      <Row className="justify-content-center mt-2">
        <Col className="col-md-5">
          <SearchBar
            placeholder="Order Reference"
            onSearch={searchHandler}
            defaultValue={searchFaultId}
          />
        </Col>
      </Row>

      <Row>
        <Col className={[styles.container, "mt-2 mb-4 py-5 px-5"].join(" ")}>
          {orderDetails && (
            <Col>
              <div className="bg-light rounded-3 p-3 mb-2 shadow-sm">
                <Row className="align-items-top g-2">
                  {/* Order Type */}
                  <Col>
                    <p style={{ fontWeight: 600, fontSize: 16 }}>
                      <span style={{ color: "#0A3D91" }}>Order Type :</span>{" "}
                      <span style={{ color: "#388E3C" }}>
                        {orderDetails.sltOrderSubType}
                      </span>
                    </p>
                  </Col>

                  {/* Service ID */}
                  <Col>
                    <p style={{ fontWeight: 600, fontSize: 16 }}>
                      <span style={{ color: "#0A3D91" }}>Service ID :</span>{" "}
                      <span style={{ color: "#388E3C" }}>
                        {orderDetails.serviceId}
                      </span>
                    </p>
                  </Col>

                  {/* Location */}
                  <Col>
                    <p style={{ fontWeight: 600, fontSize: 16 }}>
                      <span style={{ color: "#0A3D91" }}>Location :</span>{" "}
                      <span style={{ color: "#388E3C" }}>
                        {orderDetails.accountAddressCity}
                      </span>
                    </p>
                  </Col>
                </Row>
              </div>
            </Col>
          )}

          {orderDetails ? (
            <Box
              display="flex"
              flexDirection={isMobile ? "column" : "row"}
              justifyContent="center"
              alignItems="center"
              gap={2}
              mt={4}
              sx={{
                flexWrap: "wrap",
              }}
            >
              {steps.map((step, index) => (
                <Box
                  key={index}
                  display="flex"
                  alignItems="center"
                  justifyContent="flex-start"
                  width={isMobile ? "100%" : "260px"}
                  height={isMobile ? "auto" : "110px"}
                  borderRadius="15px"
                  padding="10px 20px"
                  sx={{
                    background: step.active
                      ? "linear-gradient(to right, #D4EAF7, #F2F8FF)"
                      : "#E5E5E5",
                    color: step.active ? "#0A3D91" : "#A0A0A0",
                    boxShadow: step.active ? 3 : 0,
                    transition: "0.3s",
                  }}
                >
                  <Box
                    borderRadius="50%"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    m={isMobile ? 1 : 1}
                    mr={isMobile ? 2 : 1}
                    sx={{
                      backgroundColor: step.active ? "#51B848" : "#BDBDBD",
                      color: "white",
                      fontSize:
                        step.number === 2 || step.number === 3
                          ? "16px"
                          : "18px",
                      fontWeight: "bold",
                    }}
                  >
                    {step.icon}
                  </Box>

                  <Box
                    width="3px"
                    height="35px"
                    mx={2}
                    sx={{
                      backgroundColor: step.active ? "#0A3D91" : "#A0A0A0",
                    }}
                  />

                  <Box>
                    <Typography
                      fontWeight={step.active ? "bold" : "normal"}
                      fontSize={isMobile ? "12px" : "14px"}
                      sx={{ color: step.active ? "#0A3D91" : "#A0A0A0" }}
                    >
                      {step.title}
                    </Typography>
                    {step.description && (
                      <Typography
                        fontSize={isMobile ? "10px" : "12px"}
                        sx={{ color: step.active ? "#0A3D91" : "#A0A0A0" }}
                      >
                        {step.description}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
              {orderDetails?.status &&
                ["Abandoned", "Rejected", "Cancelled"].includes(
                  orderDetails.status
                ) && (
                  <Row className="mt-3 justify-content-center">
                    <Col className="col-md-12">
                      <TextInfo color="primary">
                        <b>
                          <i
                            className="poppins-regular"
                            style={{ fontStyle: "italic" }}
                          >
                            Note:This order has been cancelled. Please contact
                            your Account Manager for further details.
                          </i>
                        </b>
                      </TextInfo>
                    </Col>
                  </Row>
                )}
            </Box>
          ) : (
            <EmptyPlaceholder />
          )}
        </Col>
      </Row>

      <Box
        sx={{
          position: "fixed",
          bottom: 24,
          right: 24,
          zIndex: 1000,
        }}
      >
        <Button
          variant="contained"
          color="primary"
          sx={{
            minWidth: "56px",
            width: "300px",
            height: "50px",
            boxShadow: 3,
            "&:hover": {
              boxShadow: 6,
            },
          }}
          onClick={() => {
            navigate(`/tracker/status/orderProgress/ongoing`);
          }}
        >
          View All Ongoing Orders
        </Button>
      </Box>

      {/* error message */}
      <AlertModal
        show={!!errorMessage}
        buttons={[
          {
            variant: "danger",
            text: "OK",
            onClick: () => {
              setErrorMessage(undefined);
            },
          },
        ]}
        variant={"error"}
      >
        {errorMessage}
      </AlertModal>
    </div>
  );
};

export default OrderProgress;
